import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Icon,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../../axios";
import usePageTitle from "../../pageTitle";

const STORAGE_KEY = "laddar_spin_participant";

const getWheelPoint = (angle, radius, center = 180) => {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: center + radius * Math.cos(radians),
    y: center + radius * Math.sin(radians),
  };
};

const getSegmentPath = (startAngle, endAngle) => {
  const start = getWheelPoint(startAngle, 170);
  const end = getWheelPoint(endAngle, 170);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    "M 180 180",
    `L ${start.x} ${start.y}`,
    `A 170 170 0 ${largeArc} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

const getSegmentColor = (segment, index) => {
  const fallbackColors = [
    "#E53935",
    "#1E88E5",
    "#43A047",
    "#FB8C00",
    "#8E24AA",
    "#00897B",
    "#F4511E",
    "#3949AB",
  ];

  return segment.color || fallbackColors[index % fallbackColors.length];
};

const getWheelLabel = (segment) => segment.title || "Prize";

function WheelPage() {
  usePageTitle("Spin & Win");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [promotion, setPromotion] = useState(null);
  const [wheel, setWheel] = useState(null);
  const [segments, setSegments] = useState([]);
  const [participant, setParticipant] = useState(null);
  const [result, setResult] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);

  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [selectedSegmentLabel, setSelectedSegmentLabel] = useState(null);

  const [winningSpinReference, setWinningSpinReference] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [participantName, setParticipantName] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [participantPhone, setParticipantPhone] = useState("");

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrorMsg(message || "An unexpected error occurred.");
  };

  useEffect(() => {
    if (!errorMsg) return;

    const timer = window.setTimeout(() => {
      setErrorMsg("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [errorMsg]);

  const getResultDisplay = () => {
    if (!result) return null;

    switch (result.segment_type) {
      case "PRIZE":
        return {
          title: "Congratulations!",
          message: `You won ${result.title || "a prize"}!`,
          emoji: "🏆",
          color: "#16A34A",
          animation: "prizePop 0.65s ease-out",
          buttonLabel: "Claim Prize",
        };

      case "EXTRA_SPINS":
        return {
          title: "Bonus Spins!",
          message: `You've won ${result.extra_spins || 0} extra spin(s)!`,
          emoji: "🎁",
          color: "#7C3AED",
          animation: "bonusPop 0.75s ease-out",
          buttonLabel: "Let's Spin Again!",
        };

      case "NO_WIN":
        return {
          title: "Better Luck Next Time!",
          message: "No prize this time. Give it another try!",
          emoji: "😔",
          color: "#64748B",
          animation: "noWinPop 0.65s ease-out",
          buttonLabel: "Close",
        };

      default:
        return {
          title: result.title || "Spin Complete",
          message: result.title || "Your spin has been completed.",
          emoji: "🎯",
          color: "#2563EB",
          animation: "prizePop 0.65s ease-out",
          buttonLabel: "Continue",
        };
    }
  };

  const getSavedParticipant = useCallback((promotionReference) => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));

      if (
        saved &&
        saved.promotion === promotionReference &&
        saved.participant
      ) {
        return saved.participant;
      }
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
    }

    return null;
  }, []);

  const saveParticipant = (promotionReference, participantData) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        promotion: promotionReference,
        participant: participantData,
      }),
    );
  };

  const loadWheel = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    setParticipant(null);
    setWheel(null);
    setPromotion(null);
    setSegments([]);
    setResult(null);
    setWinningSpinReference(null);

    try {
      const response = await axios.get(`web/tm/spin/wheel/${ref}/fetch.json`);

      if (response.data.status !== "success") {
        showMessage(response.data.data?.message);
        return;
      }

      const data = response.data.data;
      const promotionData = data.promotion;

      setWheel(data.wheel);
      setPromotion(promotionData);
      setSegments(data.segments || []);

      const savedParticipant = getSavedParticipant(promotionData.reference);

      setParticipant(savedParticipant);
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to load this wheel. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [ref, getSavedParticipant]);

  useEffect(() => {
    loadWheel();
  }, [loadWheel]);

  const registerParticipant = async () => {
    if (!promotion || submitting) return;

    setSubmitting(true);
    setErrorMsg("");

    try {
      const response = await axios.post("web/tm/spin/register.json", {
        reference: promotion.reference,
        name: participantName.trim(),
        email_address: participantEmail.trim(),
        phone_number: participantPhone.trim(),
      });

      if (response.data.status !== "success") {
        showMessage(response.data.data?.message);
        return;
      }

      const participantData = response.data.data.participant;

      saveParticipant(promotion.reference, participantData);
      setParticipant(participantData);
      setRegistrationOpen(false);

      setParticipantName("");
      setParticipantEmail("");
      setParticipantPhone("");

      showMessage(
        response.data.data.message || "Registration successful!",
        "success",
      );
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to complete registration.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const spinWheel = async () => {
    if (!participant || !wheel || !promotion || spinning || !segments.length) {
      return;
    }

    setSpinning(true);
    setErrorMsg("");
    setResult(null);
    setSelectedSegmentLabel(null);
    setWinningSpinReference(null);

    try {
      const response = await axios.post(`web/tm/spin/wheel/spin.json`, {
        reference: ref,
        promotion: promotion.reference,
        participant: participant.reference,
      });

      if (response.data.status !== "success") {
        showMessage(response.data.data?.message);
        setSpinning(false);
        return;
      }

      const winningSegment = response.data.data.segment;

      // This must be the reference of SpinPromotionWheelSpins,
      // not the winning segment's own reference.
      setWinningSpinReference(winningSegment.spin_reference || null);

      const winningIndex = segments.findIndex(
        (segment) => segment.reference === winningSegment.reference,
      );

      if (winningIndex < 0) {
        setResult(winningSegment);
        setSpinning(false);

        showMessage(
          "The spin completed, but its result could not be matched to a wheel segment.",
        );

        return;
      }

      const sliceAngle = 360 / segments.length;
      const segmentCenter = winningIndex * sliceAngle + sliceAngle / 2;
      const targetAngle = (360 - segmentCenter + 360) % 360;
      const currentAngle = ((rotation % 360) + 360) % 360;
      const adjustment = (targetAngle - currentAngle + 360) % 360;

      setRotation(rotation + 360 * 6 + adjustment);

      window.setTimeout(() => {
        setResult(winningSegment);
        setSpinning(false);
      }, 4500);
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to spin the wheel. Please try again.",
      );

      setSpinning(false);
    }
  };

  const renderWheel = () => {
    if (!segments.length) {
      return (
        <Box
          sx={{
            width: "100%",
            minHeight: 360,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 48 }}>🎰</Typography>
          <Typography mt={2} variant="button">
            Your wheel will appear here
          </Typography>
          <Typography mt={0.5} variant="caption" textAlign="center">
            No segments are available on this wheel.
          </Typography>
        </Box>
      );
    }

    const segmentAngle = 360 / segments.length;

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          mx: "auto",
          py: 2,
        }}
      >
        <svg
          viewBox="0 0 360 360"
          role="img"
          aria-label={`${wheel?.title || "Prize"} wheel`}
          style={{
            width: "100%",
            height: "auto",
            overflow: "visible",
            display: "block",
          }}
        >
          <circle
            cx="180"
            cy="180"
            r="176"
            fill="#FFFFFF"
            stroke="#DADADA"
            strokeWidth="2"
          />

          <g
            style={{
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "180px 180px",
              transition: spinning
                ? "transform 4.5s cubic-bezier(0.12, 0.75, 0.15, 1)"
                : "none",
            }}
          >
            {segments.map((segment, index) => {
              const startAngle = index * segmentAngle;
              const endAngle = (index + 1) * segmentAngle;
              const middleAngle = startAngle + segmentAngle / 2;
              const labelPoint = getWheelPoint(middleAngle, 112);
              const label = getWheelLabel(segment);
              const color = getSegmentColor(segment, index);

              const labelWidth = Math.max(
                36,
                Math.min(
                  100,
                  224 * Math.sin((segmentAngle * Math.PI) / 360) * 0.78,
                ),
              );

              const showFullLabel = () => {
                if (spinning) return;
                setSelectedSegmentLabel(label);
              };

              return (
                <g
                  key={segment.reference || index}
                  onClick={showFullLabel}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      showFullLabel();
                    }
                  }}
                  role="button"
                  tabIndex={spinning ? -1 : 0}
                  aria-label={`View full segment label: ${label}`}
                  style={{
                    cursor: spinning ? "default" : "pointer",
                    outline: "none",
                  }}
                >
                  <path
                    d={getSegmentPath(startAngle, endAngle)}
                    fill={color}
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    opacity={segment.status === false ? 0.35 : 1}
                  />

                  <foreignObject
                    x={labelPoint.x - labelWidth / 2}
                    y={labelPoint.y - 28}
                    width={labelWidth}
                    height="56"
                    transform={`rotate(${middleAngle}, ${labelPoint.x}, ${labelPoint.y})`}
                    style={{ pointerEvents: "none" }}
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "#FFFFFF",
                        fontSize:
                          segments.length > 8
                            ? 8
                            : segments.length > 5
                              ? 10
                              : 12,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        padding: 2,
                        boxSizing: "border-box",
                      }}
                    >
                      {label}
                    </div>
                  </foreignObject>
                </g>
              );
            })}
          </g>

          <circle
            cx="180"
            cy="180"
            r="39"
            fill="#FFFFFF"
            stroke="#DADADA"
            strokeWidth="2"
          />

          <circle cx="180" cy="180" r="31" fill="#202124" />

          <text
            x="180"
            y="176"
            fill="#FFFFFF"
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            SPIN
          </text>

          <text
            x="180"
            y="190"
            fill="#FFFFFF"
            fontSize="8"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            & WIN
          </text>

          <path
            d="M 168 2 L 192 2 L 180 22 Z"
            fill="#202124"
            stroke="#FFFFFF"
            strokeWidth="1"
          />
        </svg>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!wheel || !promotion) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            {errorMsg || "This wheel could not be found."}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 2, md: 2 },
        px: 2,
      }}
    >
      <Container maxWidth="md">
        {/* <Button
          disabled={spinning}
          onClick={() => navigate(`/tm/spin/${promotion.reference}`)}
          sx={{ mb: 3 }}
        >
          ← Back to promotion
        </Button> */}

        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: 3,
              fontSize: 13,
              mb: 2,
            }}
          >
            SPIN & WIN
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: 28, md: 40 },
              fontWeight: 800,
              overflowWrap: "anywhere",
            }}
          >
            {wheel.title}
          </Typography>

          {wheel.description && (
            <Typography
              sx={{
                mt: 2,
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              {wheel.description}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: { xs: 4, md: 6 },
          }}
        >
          {renderWheel()}
        </Box>

        <Stack alignItems="center" spacing={3}>
          {errorMsg && (
            <Fade in>
              <Alert
                severity={severity}
                onClose={() => setErrorMsg("")}
                sx={{ mb: 3, borderRadius: 2 }}
              >
                {errorMsg}
              </Alert>
            </Fade>
          )}

          {participant ? (
            <Button
              variant="contained"
              size="large"
              disabled={spinning || !segments.length}
              onClick={spinWheel}
              sx={{
                minWidth: 240,
                py: 1.6,
                borderRadius: 2,
                fontSize: 17,
                fontWeight: 800,
              }}
            >
              {spinning ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "SPIN NOW"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={() => setRegistrationOpen(true)}
              sx={{
                minWidth: 240,
                py: 1.6,
                borderRadius: 2,
                fontSize: 17,
                fontWeight: 800,
              }}
            >
              Register
            </Button>
          )}
        </Stack>

        <Typography textAlign="center" variant="body2" sx={{ mt: 5 }}>
          {participant ? (
            <>
              <small>Playing as</small>
              <br />
              {participant.name}
            </>
          ) : (
            "Register to participate"
          )}
        </Typography>
      </Container>

      {/* Full segment label dialog */}
      <Dialog
        open={selectedSegmentLabel !== null}
        onClose={() => setSelectedSegmentLabel(null)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="segment-label-dialog-title"
      >
        <DialogContent>
          <Typography
            id="segment-label-dialog-title"
            sx={{
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.7,
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {selectedSegmentLabel}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setSelectedSegmentLabel(null)}
            variant="contained"
            color="primary"
            startIcon={<Icon>close</Icon>}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Participant registration dialog */}
      <Dialog
        open={registrationOpen}
        onClose={() => {
          if (!submitting) setRegistrationOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Register to Participate
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 3 }}>
            Enter your details to register for this promotion.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Full Name"
              value={participantName}
              onChange={(event) => setParticipantName(event.target.value)}
              fullWidth
              required
              autoComplete="name"
            />

            <TextField
              label="Email Address"
              type="email"
              value={participantEmail}
              onChange={(event) => setParticipantEmail(event.target.value)}
              fullWidth
              required
              autoComplete="email"
            />

            <TextField
              label="Phone Number"
              value={participantPhone}
              onChange={(event) => setParticipantPhone(event.target.value)}
              fullWidth
              required
              autoComplete="tel"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            disabled={submitting}
            onClick={() => setRegistrationOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={
              submitting ||
              !participantName.trim() ||
              !participantEmail.trim() ||
              !participantPhone.trim()
            }
            onClick={registerParticipant}
          >
            {submitting ? <CircularProgress size={22} /> : "Register"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Animated spin result dialog */}
      <Dialog
        open={Boolean(result)}
        onClose={() => setResult(null)}
        TransitionComponent={Fade}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: "#FFFFFF",
            color: "#111827",
            borderRadius: 3,
            overflow: "hidden",
            textAlign: "center",
          },
        }}
      >
        {result &&
          (() => {
            const display = getResultDisplay();
            const isPrize = result.segment_type === "PRIZE";
            const isExtraSpins = result.segment_type === "EXTRA_SPINS";

            return (
              <DialogContent
                sx={{
                  px: 4,
                  py: 5,
                  position: "relative",
                  overflow: "hidden",
                  "@keyframes prizePop": {
                    "0%": {
                      transform: "scale(0.3) rotate(-15deg)",
                      opacity: 0,
                    },
                    "70%": {
                      transform: "scale(1.12) rotate(5deg)",
                      opacity: 1,
                    },
                    "100%": {
                      transform: "scale(1) rotate(0)",
                      opacity: 1,
                    },
                  },
                  "@keyframes bonusPop": {
                    "0%": {
                      transform: "scale(0.35) rotate(-180deg)",
                      opacity: 0,
                    },
                    "70%": {
                      transform: "scale(1.12) rotate(8deg)",
                      opacity: 1,
                    },
                    "100%": {
                      transform: "scale(1) rotate(0)",
                      opacity: 1,
                    },
                  },
                  "@keyframes noWinPop": {
                    "0%": {
                      transform: "translateY(-22px) scale(0.85)",
                      opacity: 0,
                    },
                    "60%": {
                      transform: "translateY(5px) scale(1.03)",
                      opacity: 1,
                    },
                    "100%": {
                      transform: "translateY(0) scale(1)",
                      opacity: 1,
                    },
                  },
                  "@keyframes confettiFall": {
                    "0%": {
                      transform: "translateY(-25px) rotate(0deg)",
                      opacity: 1,
                    },
                    "100%": {
                      transform: "translateY(190px) rotate(540deg)",
                      opacity: 0,
                    },
                  },
                  "@keyframes glowPulse": {
                    "0%, 100%": {
                      boxShadow: "0 0 0 0 rgba(22,163,74,0.18)",
                    },
                    "50%": {
                      boxShadow: "0 0 0 18px rgba(22,163,74,0)",
                    },
                  },
                }}
              >
                {isPrize &&
                  ["🎉", "✨", "🎊", "⭐", "🎉", "✨", "🎊"].map(
                    (item, index) => (
                      <Box
                        key={`${item}-${index}`}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: `${6 + index * 14}%`,
                          fontSize: 22,
                          animation: `confettiFall ${
                            1.5 + (index % 3) * 0.35
                          }s ease-in forwards`,
                          animationDelay: `${index * 0.1}s`,
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      >
                        {item}
                      </Box>
                    ),
                  )}

                <Box
                  sx={{
                    width: 112,
                    height: 112,
                    mx: "auto",
                    mb: 3,
                    borderRadius: "50%",
                    bgcolor: `${display.color}15`,
                    border: `2px solid ${display.color}35`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 58,
                    animation: `${display.animation}${
                      isPrize ? ", glowPulse 1.5s ease-in-out 0.6s 2" : ""
                    }`,
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  {display.emoji}
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: display.color,
                    mb: 1.5,
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  {display.title}
                </Typography>

                <Typography
                  sx={{
                    color: "#475569",
                    fontSize: 16,
                    lineHeight: 1.7,
                    mb: 3,
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  {display.message}
                </Typography>

                {isExtraSpins && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 3,
                      py: 1.5,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: "#F3E8FF",
                      color: "#6D28D9",
                      fontWeight: 800,
                      border: "1px solid #DDD6FE",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 28,
                        fontWeight: 900,
                        color: "#6D28D9",
                      }}
                    >
                      +{result.extra_spins || 0}
                    </Typography>

                    <Typography sx={{ fontWeight: 700 }}>
                      Extra Spins
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    if (isPrize) {
                      if (!winningSpinReference) {
                        showMessage(
                          "The spin reference is missing. Please contact the promotion organiser.",
                        );
                        return;
                      }

                      setResult(null);

                      // Matches: /tm/spin/disburse/:ref
                      navigate(`/tm/spin/disburse/${winningSpinReference}`);
                      return;
                    }

                    setResult(null);
                  }}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: display.color,
                    color: "#FFFFFF",
                    fontWeight: 800,
                    textTransform: "none",
                    boxShadow: `0 4px 12px ${display.color}35`,
                    "&:hover": {
                      bgcolor: display.color,
                      filter: "brightness(0.9)",
                      boxShadow: `0 6px 16px ${display.color}45`,
                    },
                  }}
                >
                  {isPrize ? "Claim Prize" : display.buttonLabel}
                </Button>
              </DialogContent>
            );
          })()}
      </Dialog>
    </Box>
  );
}

export default WheelPage;
